import type { FormInstance } from 'antd';
import { message } from 'antd';
import { Moment } from 'moment';
import { create } from 'zustand';

import produce from 'immer';
import GetProductQualities from '~/@core/application/useCases/inputs/product/getProductQualities';
import GetProductsPreferenceColor from '~/@core/application/useCases/inputs/product/getProductsPreferenceColor';
import Customers from '~/@core/domain/model/customer/Customers';
import type IProductCategory from '~/@core/domain/model/product/ProductCategory';
import ProductPreferenceColor from '~/@core/domain/model/product/ProductPreferenceColor';
import ProductQuality from '~/@core/domain/model/product/ProductQuality';
import type Site from '~/@core/domain/model/site/Site';
import { fetchSites as fetchSitesService } from '~/@core/infrastructure/api/services/siteService';
import { PermissionNames } from '~/constants/permissionNames';
import { createInitialTableState, INITIAL_DATA_STATE } from '~/constants/table';
import { ISummarySaleStatement, SaleStatement } from '~/domain/api';
import { SaleStatementDateType } from '~/domain/enum/salesStatement/saleStatementDateType.enum';
import {
  SaleStatementTypeEnum,
  SaleStatementTypeIdEnum,
} from '~/domain/enum/salesStatement/saleStatementType.enum';
import { RecipientDetailed } from '~/domain/model';
import { ISiteIdParams } from '~/domain/model/PriceReport/PriceReportService';
import type IProductGroupWithCategories from '~/domain/model/PriceReport/ProductGroupWithCategories';
import type { IProductSimple } from '~/domain/model/PriceReport/ProductSimple';
import { ISalesChannel } from '~/domain/model/PriceReport/SaleChannel';
import {
  IPagedSalesStatementReportParams,
  ISalesStatementCardsPanel,
  ISalesStatementExportColumn,
  ISalesStatementExportReportParams,
  ISalesStatementReportParams,
  ISalesStatementSummaryExportColumn,
  ISalesStatementSummaryExportReportParams,
} from '~/domain/model/SalesStatement/SalesStatement';
import { getAllCustomers } from '~/services/producer/customer/CustomerService';
import {
  getProductRecipients,
  getSaleChannels as getSaleChannelsService,
} from '~/services/producer/PriceReport/PriceReportService';
import {
  getProductCategories,
  getProductGroups,
  getProductsSimple,
} from '~/services/producer/ProductFilter/ProductFilterService';
import {
  fetchSaleStatementCardPanel,
  getSummaryTable,
  getTable,
} from '~/services/producer/SalesStatement/SalesStatementService';
import { IObjectDataState } from '~/types/data';
import { ITableDataState, ITableState } from '~/types/table';
import { ColumnPreferenceScopes } from '~/constants/storageKeys';
import {
  clearColumnPreferences,
  getVisibleColumnKeys,
  saveVisibleColumnKeys,
} from '~/helpers/util/columnPreferences';

export type IFormFilter = {
  saleChannels: number;
  subSaleChannels: number[];
  siteIds: number[];
  dateType: SaleStatementDateType | undefined;
  filteredDate: [Moment | null, Moment | null] | null;
  productIds: number[];
  categoryIds: number[];
  groupIds: number[];
  sizes: string[];
  colorIds: number[];
  quality: string | null;
  customerIds: number[];
  origin: number[];
};

export type ITable = ITableState<SaleStatement>;

export type ISummaryTable = ITableState<ISummarySaleStatement>;

export type IActions = {
  setData: (fn: (state: ISalesStatement) => void) => void;
  setIntermediationFormInstance: (form: Partial<FormInstance<IFormFilter>>) => void;
  setKlokFormInstance: (form: Partial<FormInstance<IFormFilter>>) => void;
  fetchSites: () => Promise<Site[]>;
  fetchSaleChannels: () => Promise<ISalesChannel[]>;
  getCustomers: () => Promise<Customers[]>;
  getCategories: (type: SaleStatementTypeEnum) => Promise<IProductCategory[]>;
  getProducts: (type: SaleStatementTypeEnum) => Promise<IProductSimple[]>;
  getSizes: (type: SaleStatementTypeEnum) => Promise<RecipientDetailed[]>;
  getGroups: (type: SaleStatementTypeEnum) => Promise<IProductGroupWithCategories[]>;
  getColors: () => Promise<ProductPreferenceColor[]>;
  getQualities: () => Promise<ProductQuality[]>;
  mappedSiteIds: (type: SaleStatementTypeEnum) => number[];
  resetStatesToDefault: () => void;
  toggleColumnVisibility: (type: SaleStatementTypeEnum, key: string) => void;
  resetColumnVisibility: (type: SaleStatementTypeEnum) => void;
  onSelectOrClearSite: (type: SaleStatementTypeEnum) => void;
  toggleFilterVisibility: (type: SaleStatementTypeEnum) => void;
  loadMainFilters: () => void;
  loadHiddenFilters: (type: SaleStatementTypeEnum) => void;
  handleSearch: (type: SaleStatementTypeEnum) => void;
  handleIntermediationCardPanel: () => void;
  handleKlokCardPanel: () => void;
  handleIntermediationTable: () => void;
  handleKlokTable: () => void;
  handleIntermediationSummaryTable: () => void;
  handleKlokSummaryTable: () => void;
};

export type IGetters = {
  getParams(type: SaleStatementTypeEnum): ISalesStatementReportParams;
  getExportParams(
    type: SaleStatementTypeEnum,
    columns: ISalesStatementExportColumn,
  ): ISalesStatementExportReportParams;
  getSummaryExportParams(
    type: SaleStatementTypeEnum,
    columns: ISalesStatementSummaryExportColumn,
  ): ISalesStatementSummaryExportReportParams;
  getIntermediationPagedParams(): IPagedSalesStatementReportParams;
  getKlokPagedParams(): IPagedSalesStatementReportParams;
};

export type IFilters = {
  saleChannels: ITableDataState<ISalesChannel>;
  sites: ITableDataState<Site>;
  products: ITableDataState<IProductSimple>;
  categories: ITableDataState<IProductCategory>;
  groups: ITableDataState<IProductGroupWithCategories>;
  sizes: ITableDataState<RecipientDetailed>;
  colors: ITableDataState<ProductPreferenceColor>;
  qualities: ITableDataState<ProductQuality>;
  customers: ITableDataState<Customers>;
  formFilter: IFormFilter;
};

export type ISalesStatement = {
  intermediationTable: ITable;
  klokTable: ITable;
  intermediationSummaryTable: ISummaryTable;
  klokSummaryTable: ISummaryTable;
  intermediationCardPanel: IObjectDataState<ISalesStatementCardsPanel>;
  klokCardPanel: IObjectDataState<ISalesStatementCardsPanel>;
  filters: IFilters;
  intermediationFormFilterInstance: Partial<FormInstance<IFormFilter>> | null;
  klokFormFilterInstance: Partial<FormInstance<IFormFilter>> | null;
  isMainFiltersSuccessful: boolean;
  isHiddenFiltersSucessful: boolean;
  showHiddenFilters: boolean;
  intermediationVisibleColumnKeys: string[];
  klokVisibleColumnKeys: string[];
} & IActions &
  IGetters;

export const defaultColumnOrderBy = 'transactionDate';

export const INTERMEDIATION_ALL_COLUMN_KEYS = [
  'salesChannel',
  'transactionDate',
  'billingDate',
  'commercialDate',
  'orderNumber',
  'siteCode',
  'customerDescription',
  'productDescription',
  'qualityDescription',
  'packingCode',
  'quantity',
  'totalQuantity',
  'unitValue',
  'packingValue',
  'freightValue',
  'totalValue',
] as const;

export const KLOK_ALL_COLUMN_KEYS = [
  'salesChannel',
  'transactionDate',
  'billingDate',
  'commercialDate',
  'orderNumber',
  'gfpNumber',
  'siteCode',
  'customerDescription',
  'productDescription',
  'qualityDescription',
  'packingCode',
  'quantity',
  'totalQuantity',
  'unitValue',
  'packingValue',
  'freightValue',
  'totalValue',
] as const;

export const INTERMEDIATION_COLUMN_PREFERENCE_SCOPE =
  ColumnPreferenceScopes.SalesStatementIntermediation;
export const KLOK_COLUMN_PREFERENCE_SCOPE = ColumnPreferenceScopes.SalesStatementKlok;

export const getIntermediationVisibleColumnKeys = (): string[] =>
  getVisibleColumnKeys(INTERMEDIATION_COLUMN_PREFERENCE_SCOPE, INTERMEDIATION_ALL_COLUMN_KEYS);

export const getKlokVisibleColumnKeys = (): string[] =>
  getVisibleColumnKeys(KLOK_COLUMN_PREFERENCE_SCOPE, KLOK_ALL_COLUMN_KEYS);

export const initialStateTable: ITable = createInitialTableState<SaleStatement>({
  orderBy: defaultColumnOrderBy,
  pageSize: 100,
});

export const initialStateSummaryTable: ISummaryTable =
  createInitialTableState<ISummarySaleStatement>({
    orderBy: defaultColumnOrderBy,
    pageSize: 100,
  });

export const initialStateFilters: IFilters = {
  formFilter: {
    saleChannels: Number.NaN,
    subSaleChannels: [],
    siteIds: [],
    productIds: [],
    categoryIds: [],
    groupIds: [],
    sizes: [],
    colorIds: [],
    quality: null,
    filteredDate: null,
    dateType: undefined,
    customerIds: [],
    origin: [],
  },
  saleChannels: INITIAL_DATA_STATE,
  sites: INITIAL_DATA_STATE,
  products: INITIAL_DATA_STATE,
  categories: INITIAL_DATA_STATE,
  groups: INITIAL_DATA_STATE,
  sizes: INITIAL_DATA_STATE,
  colors: INITIAL_DATA_STATE,
  qualities: INITIAL_DATA_STATE,
  customers: INITIAL_DATA_STATE,
};

export const initialState = {
  formFilterInstance: null,
};

export const initialCardPanelState = {
  data: {
    uniqueId: '',
    totalValue: 0,
    totalQuantity: 0,
    salesChannel: '-',
  },
  isError: false,
  isLoading: false,
  isSuccessful: false,
};

const PermissionName = PermissionNames.Reports.MenuSalesStatement;

const hasElements = <T>(_array: T[]): boolean => Array.isArray(_array) && _array.length > 0;

const useSaleStatementByTypeStore = create<ISalesStatement>()((set, get) => ({
  // States
  intermediationTable: initialStateTable,
  klokTable: initialStateTable,
  intermediationSummaryTable: initialStateSummaryTable,
  klokSummaryTable: initialStateSummaryTable,
  intermediationCardPanel: initialCardPanelState,
  klokCardPanel: initialCardPanelState,
  filters: initialStateFilters,
  intermediationFormFilterInstance: null,
  klokFormFilterInstance: null,
  formFilterInstance: initialState.formFilterInstance,
  isMainFiltersSuccessful: false,
  isHiddenFiltersSucessful: false,
  showHiddenFilters: false,
  intermediationVisibleColumnKeys: getIntermediationVisibleColumnKeys(),
  klokVisibleColumnKeys: getKlokVisibleColumnKeys(),

  // Setters
  setData: fn => set(produce(fn)),
  setIntermediationFormInstance: form => {
    set({ intermediationFormFilterInstance: form });
  },
  setKlokFormInstance: form => {
    set({ klokFormFilterInstance: form });
  },

  // Getters
  getParams: (type: SaleStatementTypeEnum) => {
    const formValues =
      type === SaleStatementTypeEnum.Klok
        ? get().klokFormFilterInstance?.getFieldsValue?.()
        : get().intermediationFormFilterInstance?.getFieldsValue?.();
    const startDate = formValues?.filteredDate?.[0]?.format('YYYY-MM-DD');
    const endDate = formValues?.filteredDate?.[1]?.format('YYYY-MM-DD');
    const saleChannel =
      type === SaleStatementTypeEnum.Klok
        ? SaleStatementTypeIdEnum.Klok
        : SaleStatementTypeIdEnum.Intermediation;

    return {
      StartDate: startDate,
      EndDate: endDate,
      DateType: formValues?.dateType,
      SaleChannel: saleChannel,
      SubSaleChannels: formValues?.origin,
      SiteIds: formValues?.siteIds,
      CustomerIds: formValues?.customerIds,
      ProductIds: formValues?.productIds,
      CategoryIds: formValues?.categoryIds,
      GroupIds: formValues?.groupIds,
      Sizes: formValues?.sizes,
      ColorIds: formValues?.colorIds,
      Quality: formValues?.quality,
      PermissionName,
    } as ISalesStatementReportParams;
  },
  getExportParams: (type: SaleStatementTypeEnum, columns: ISalesStatementExportColumn) => {
    return {
      Request: get().getParams(type),
      Columns: columns,
    } as ISalesStatementExportReportParams;
  },
  getSummaryExportParams: (
    type: SaleStatementTypeEnum,
    columns: ISalesStatementSummaryExportColumn,
  ) => {
    return {
      Request: get().getParams(type),
      Columns: columns,
    } as ISalesStatementSummaryExportReportParams;
  },
  getIntermediationPagedParams: () => {
    const tableParams = get().intermediationTable.tableParams;

    return {
      Data: get().getParams(SaleStatementTypeEnum.Intermediation),
      PageSize: tableParams.pagination.pageSize ?? 100,
      PageNumber: tableParams.pagination.current ?? 1,
      OrderBy: tableParams.orderBy ?? defaultColumnOrderBy,
      Sort: tableParams.sort ?? 0,
    } as IPagedSalesStatementReportParams;
  },
  getKlokPagedParams: () => {
    const tableParams = get().klokTable.tableParams;

    return {
      Data: get().getParams(SaleStatementTypeEnum.Klok),
      PageSize: tableParams.pagination.pageSize ?? 100,
      PageNumber: tableParams.pagination.current ?? 1,
      OrderBy: tableParams.orderBy ?? defaultColumnOrderBy,
      Sort: tableParams.sort ?? 0,
    } as IPagedSalesStatementReportParams;
  },

  // Actions
  fetchSites: async () => {
    try {
      get().setData(state => {
        state.filters.sites.isLoading = true;
        state.filters.sites.isSuccessful = false;
        state.filters.sites.isError = false;
      });

      const data = await fetchSitesService(PermissionName);

      get().setData(state => {
        state.filters.sites.data = data || [];
        state.filters.sites.isSuccessful = true;
        state.filters.sites.isError = false;
        state.filters.sites.isLoading = false;
      });

      return Promise.resolve(data);
    } catch (error) {
      get().setData(state => {
        state.filters.sites.data = [];
        state.filters.sites.isSuccessful = false;
        state.filters.sites.isError = true;
        state.filters.sites.isLoading = false;
      });

      return Promise.reject(error);
    }
  },
  fetchSaleChannels: async () => {
    const { setData } = get();

    try {
      setData(state => {
        state.filters.saleChannels.isLoading = true;
        state.filters.saleChannels.isSuccessful = false;
        state.filters.saleChannels.isError = false;
      });

      const response = await getSaleChannelsService();
      const data = response?.data ?? [];

      setData(state => {
        state.filters.saleChannels.data = data;
        state.filters.saleChannels.isSuccessful = true;
        state.filters.saleChannels.isError = false;
        state.filters.saleChannels.isLoading = false;
      });

      return Promise.resolve(data);
    } catch (error) {
      setData(state => {
        state.filters.saleChannels.data = [];
        state.filters.saleChannels.isSuccessful = false;
        state.filters.saleChannels.isError = true;
        state.filters.saleChannels.isLoading = false;
      });

      return Promise.reject(error);
    }
  },
  getCustomers: async () => {
    try {
      get().setData(state => {
        state.filters.customers.isLoading = true;
        state.filters.customers.isSuccessful = false;
        state.filters.customers.isError = false;
      });

      const { data } = await getAllCustomers();

      get().setData(state => {
        state.filters.customers.data = data ?? [];
        state.filters.customers.isSuccessful = true;
        state.filters.customers.isError = false;
        state.filters.customers.isLoading = false;
      });

      return Promise.resolve(data);
    } catch (error) {
      get().setData(state => {
        state.filters.customers.data = [];
        state.filters.customers.isSuccessful = false;
        state.filters.customers.isError = true;
        state.filters.customers.isLoading = false;
      });

      return Promise.reject(error);
    }
  },
  getCategories: async (type: SaleStatementTypeEnum) => {
    const { setData } = get();

    setData(state => {
      state.filters.categories.isLoading = true;
      state.filters.categories.isSuccessful = false;
      state.filters.categories.isError = false;
    });
    try {
      const params = {
        siteIds: get().mappedSiteIds(type),
      } as ISiteIdParams;

      const { data } = await getProductCategories(params, PermissionName);

      setData(state => {
        state.filters.categories.data = data;
        state.filters.categories.isSuccessful = true;
        state.filters.categories.isError = false;
        state.filters.categories.isLoading = false;
      });

      return Promise.resolve(data);
    } catch (error) {
      setData(state => {
        state.filters.categories.data = [];
        state.filters.categories.isSuccessful = false;
        state.filters.categories.isError = true;
        state.filters.categories.isLoading = false;
      });

      return Promise.reject(error);
    }
  },
  getGroups: async (type: SaleStatementTypeEnum) => {
    const { setData } = get();

    setData(state => {
      state.filters.groups.isLoading = true;
      state.filters.groups.isSuccessful = false;
      state.filters.groups.isError = false;
    });
    try {
      const params = {
        siteIds: get().mappedSiteIds(type),
      } as ISiteIdParams;

      const { data } = await getProductGroups(params, PermissionName);

      setData(state => {
        state.filters.groups.data = data;
        state.filters.groups.isSuccessful = true;
        state.filters.groups.isError = false;
        state.filters.groups.isLoading = false;
      });

      return Promise.resolve(data);
    } catch (error) {
      setData(state => {
        state.filters.groups.data = [];
        state.filters.groups.isSuccessful = false;
        state.filters.groups.isError = true;
        state.filters.groups.isLoading = false;
      });

      return Promise.reject(error);
    }
  },
  getProducts: async (type: SaleStatementTypeEnum) => {
    const { setData } = get();

    setData(state => {
      state.filters.products.isLoading = true;
      state.filters.products.isSuccessful = false;
      state.filters.products.isError = false;
    });
    try {
      const params = {
        siteIds: get().mappedSiteIds(type),
      } as ISiteIdParams;

      const { data } = await getProductsSimple(params, PermissionName);

      setData(state => {
        state.filters.products.data = data;
        state.filters.products.isSuccessful = true;
        state.filters.products.isError = false;
        state.filters.products.isLoading = false;
      });

      return Promise.resolve(data);
    } catch (error) {
      setData(state => {
        state.filters.products.data = [];
        state.filters.products.isSuccessful = false;
        state.filters.products.isError = true;
        state.filters.products.isLoading = false;
      });

      return Promise.reject(error);
    }
  },
  getSizes: async (type: SaleStatementTypeEnum) => {
    const { setData } = get();

    setData(state => {
      state.filters.sizes.isLoading = true;
      state.filters.sizes.isSuccessful = false;
      state.filters.sizes.isError = false;
    });
    try {
      const params = {
        siteIds: get().mappedSiteIds(type),
        permissionName: PermissionName,
      } as ISiteIdParams;

      const { data } = await getProductRecipients(params);

      setData(state => {
        state.filters.sizes.data = data;
        state.filters.sizes.isSuccessful = true;
        state.filters.sizes.isError = false;
        state.filters.sizes.isLoading = false;
      });

      return Promise.resolve(data);
    } catch (error) {
      setData(state => {
        state.filters.sizes.data = [];
        state.filters.sizes.isSuccessful = false;
        state.filters.sizes.isError = true;
        state.filters.sizes.isLoading = false;
      });

      return Promise.reject(error);
    }
  },
  getColors: async () => {
    const { setData } = get();

    setData(state => {
      state.filters.colors.isLoading = true;
      state.filters.colors.isSuccessful = false;
      state.filters.colors.isError = false;
    });
    try {
      const data = await new GetProductsPreferenceColor().execute();

      setData(state => {
        state.filters.colors.data = data;
        state.filters.colors.isSuccessful = true;
        state.filters.colors.isError = false;
        state.filters.colors.isLoading = false;
      });

      return Promise.resolve(data);
    } catch (error) {
      setData(state => {
        state.filters.colors.data = [];
        state.filters.colors.isSuccessful = false;
        state.filters.colors.isError = true;
        state.filters.colors.isLoading = false;
      });

      return Promise.reject(error);
    }
  },
  getQualities: async () => {
    const { setData } = get();

    setData(state => {
      state.filters.qualities.isLoading = true;
      state.filters.qualities.isSuccessful = false;
      state.filters.qualities.isError = false;
    });
    try {
      const data = await new GetProductQualities().execute();

      setData(state => {
        state.filters.qualities.data = data;
        state.filters.qualities.isSuccessful = true;
        state.filters.qualities.isError = false;
        state.filters.qualities.isLoading = false;
      });

      return Promise.resolve(data);
    } catch (error) {
      setData(state => {
        state.filters.qualities.data = [];
        state.filters.qualities.isSuccessful = false;
        state.filters.qualities.isError = true;
        state.filters.qualities.isLoading = false;
      });

      return Promise.reject(error);
    }
  },
  mappedSiteIds: (type: SaleStatementTypeEnum) => {
    const sites = get().filters?.sites?.data ?? [];
    const siteIdsFormSelected =
      type === SaleStatementTypeEnum.Intermediation
        ? get().intermediationFormFilterInstance?.getFieldValue?.('siteIds') ?? []
        : get().klokFormFilterInstance?.getFieldValue?.('siteIds') ?? [];

    if (!hasElements(siteIdsFormSelected)) {
      return sites.map(site => site.id);
    }

    return siteIdsFormSelected;
  },
  handleIntermediationCardPanel: async () => {
    const { setData, getParams } = get();

    try {
      setData(state => {
        state.intermediationCardPanel.isLoading = true;
        state.intermediationCardPanel.isSuccessful = true;
        state.intermediationCardPanel.isError = true;
      });
      const params = getParams(SaleStatementTypeEnum.Intermediation);
      const { data } = await fetchSaleStatementCardPanel(params);

      setData(state => {
        state.intermediationCardPanel.data = data;
        state.intermediationCardPanel.isLoading = false;
        state.intermediationCardPanel.isSuccessful = true;
        state.intermediationCardPanel.isError = false;
      });
      return Promise.resolve();
    } catch (error) {
      setData(state => {
        state.intermediationCardPanel.data = {
          uniqueId: '',
          totalQuantity: 0,
          totalValue: 0,
          salesChannel: '-',
        };
        state.intermediationCardPanel.isSuccessful = false;
        state.intermediationCardPanel.isError = true;
        state.intermediationCardPanel.isLoading = false;
      });

      message.error('Não foi possível carregar informações do painel de resumo.');

      return Promise.reject(error);
    }
  },
  handleKlokCardPanel: async () => {
    const { setData, getParams } = get();

    try {
      setData(state => {
        state.klokCardPanel.isLoading = true;
        state.klokCardPanel.isSuccessful = true;
        state.klokCardPanel.isError = true;
      });
      const params = getParams(SaleStatementTypeEnum.Klok);
      const { data } = await fetchSaleStatementCardPanel(params);

      setData(state => {
        state.klokCardPanel.data = data;
        state.klokCardPanel.isLoading = false;
        state.klokCardPanel.isSuccessful = true;
        state.klokCardPanel.isError = false;
      });
      return Promise.resolve();
    } catch (error) {
      setData(state => {
        state.klokCardPanel.data = {
          uniqueId: '',
          totalQuantity: 0,
          totalValue: 0,
          salesChannel: '-',
        };
        state.klokCardPanel.isSuccessful = false;
        state.klokCardPanel.isError = true;
        state.klokCardPanel.isLoading = false;
      });

      message.error('Não foi possível carregar informações do painel de resumo.');

      return Promise.reject(error);
    }
  },
  handleSearch: (type: SaleStatementTypeEnum) => {
    const { setData, handleIntermediationTable, handleKlokTable } = get();

    if (type === SaleStatementTypeEnum.Intermediation) {
      setData(state => {
        state.intermediationTable.tableParams.pagination.current = 1;
      });
      handleIntermediationTable();
    } else {
      setData(state => {
        state.klokTable.tableParams.pagination.current = 1;
      });
      handleKlokTable();
    }
  },
  handleIntermediationTable: async () => {
    const { setData, getIntermediationPagedParams } = get();

    try {
      setData(state => {
        state.intermediationTable.isLoading = true;
        state.intermediationTable.isSuccessful = false;
        state.intermediationTable.isError = false;
      });

      const params = getIntermediationPagedParams();

      const response = await getTable(params);

      const data = response?.data || { results: [], totalCount: 0 };

      setData(state => {
        state.intermediationTable.data = data.results || [];
        state.intermediationTable.tableParams.pagination.total = data.totalCount || 0;
        state.intermediationTable.isSuccessful = true;
        state.intermediationTable.isError = false;
        state.intermediationTable.isLoading = false;
      });

      return Promise.resolve(data.results);
    } catch (error) {
      setData(state => {
        state.intermediationTable.data = [];
        state.intermediationTable.tableParams.pagination.total = 0;
        state.intermediationTable.isSuccessful = false;
        state.intermediationTable.isError = true;
        state.intermediationTable.isLoading = false;
      });

      message.error('Não foi possível carregar a tabela.');

      return Promise.reject(error);
    }
  },
  handleKlokTable: async () => {
    const { setData, getKlokPagedParams } = get();

    try {
      setData(state => {
        state.klokTable.isLoading = true;
        state.klokTable.isSuccessful = false;
        state.klokTable.isError = false;
      });

      const params = getKlokPagedParams();

      const response = await getTable(params);

      const data = response?.data || { results: [], totalCount: 0 };

      setData(state => {
        state.klokTable.data = data.results || [];
        state.klokTable.tableParams.pagination.total = data.totalCount || 0;
        state.klokTable.isSuccessful = true;
        state.klokTable.isError = false;
        state.klokTable.isLoading = false;
      });

      return Promise.resolve(data.results);
    } catch (error) {
      setData(state => {
        state.klokTable.data = [];
        state.klokTable.tableParams.pagination.total = 0;
        state.klokTable.isSuccessful = false;
        state.klokTable.isError = true;
        state.klokTable.isLoading = false;
      });

      message.error('Não foi possível carregar a tabela.');

      return Promise.reject(error);
    }
  },
  handleIntermediationSummaryTable: async () => {
    const { setData, getParams } = get();
    const tableParams = get().intermediationSummaryTable.tableParams;

    try {
      setData(state => {
        state.intermediationSummaryTable.isLoading = true;
        state.intermediationSummaryTable.isSuccessful = false;
        state.intermediationSummaryTable.isError = false;
      });

      const params = {
        Data: getParams(SaleStatementTypeEnum.Intermediation),
        PageSize: tableParams.pagination.pageSize ?? 100,
        PageNumber: tableParams.pagination.current ?? 1,
        OrderBy: tableParams.orderBy ?? defaultColumnOrderBy,
        Sort: tableParams.sort ?? 0,
      } as IPagedSalesStatementReportParams;
      const response = await getSummaryTable(params);

      const data = response?.data || { results: [], totalCount: 0 };

      setData(state => {
        state.intermediationSummaryTable.data = data.results || [];
        state.intermediationSummaryTable.tableParams.pagination.total = data.totalCount || 0;
        state.intermediationSummaryTable.isSuccessful = true;
        state.intermediationSummaryTable.isError = false;
        state.intermediationSummaryTable.isLoading = false;
      });

      return Promise.resolve(data.results);
    } catch (error) {
      setData(state => {
        state.intermediationSummaryTable.data = [];
        state.intermediationSummaryTable.tableParams.pagination.total = 0;
        state.intermediationSummaryTable.isSuccessful = false;
        state.intermediationSummaryTable.isError = true;
        state.intermediationSummaryTable.isLoading = false;
      });

      message.error('Não foi possível carregar a tabela.');

      return Promise.reject(error);
    }
  },
  handleKlokSummaryTable: async () => {
    const { setData, getParams } = get();
    const tableParams = get().klokSummaryTable.tableParams;

    try {
      setData(state => {
        state.klokSummaryTable.isLoading = true;
        state.klokSummaryTable.isSuccessful = false;
        state.klokSummaryTable.isError = false;
      });

      const params = {
        Data: getParams(SaleStatementTypeEnum.Klok),
        PageSize: tableParams.pagination.pageSize ?? 100,
        PageNumber: tableParams.pagination.current ?? 1,
        OrderBy: tableParams.orderBy ?? defaultColumnOrderBy,
        Sort: tableParams.sort ?? 0,
      } as IPagedSalesStatementReportParams;

      const response = await getSummaryTable(params);

      const data = response?.data || { results: [], totalCount: 0 };

      setData(state => {
        state.klokSummaryTable.data = data.results || [];
        state.klokSummaryTable.tableParams.pagination.total = data.totalCount || 0;
        state.klokSummaryTable.isSuccessful = true;
        state.klokSummaryTable.isError = false;
        state.klokSummaryTable.isLoading = false;
      });

      return Promise.resolve(data.results);
    } catch (error) {
      setData(state => {
        state.klokSummaryTable.data = [];
        state.klokSummaryTable.tableParams.pagination.total = 0;
        state.klokSummaryTable.isSuccessful = false;
        state.klokSummaryTable.isError = true;
        state.klokSummaryTable.isLoading = false;
      });

      message.error('Não foi possível carregar a tabela.');

      return Promise.reject(error);
    }
  },
  toggleColumnVisibility: (type: SaleStatementTypeEnum, key: string) => {
    const isKlok = type === SaleStatementTypeEnum.Klok;
    const stateKey = isKlok ? 'klokVisibleColumnKeys' : 'intermediationVisibleColumnKeys';

    get().setData(state => {
      const current = state[stateKey];
      state[stateKey] = current.includes(key) ? current.filter(k => k !== key) : [...current, key];
    });

    saveVisibleColumnKeys(
      isKlok ? KLOK_COLUMN_PREFERENCE_SCOPE : INTERMEDIATION_COLUMN_PREFERENCE_SCOPE,
      isKlok ? KLOK_ALL_COLUMN_KEYS : INTERMEDIATION_ALL_COLUMN_KEYS,
      get()[stateKey],
    );
  },
  resetColumnVisibility: (type: SaleStatementTypeEnum) => {
    const isKlok = type === SaleStatementTypeEnum.Klok;

    clearColumnPreferences(
      isKlok ? KLOK_COLUMN_PREFERENCE_SCOPE : INTERMEDIATION_COLUMN_PREFERENCE_SCOPE,
    );

    get().setData(state => {
      if (isKlok) {
        state.klokVisibleColumnKeys = [...KLOK_ALL_COLUMN_KEYS];
      } else {
        state.intermediationVisibleColumnKeys = [...INTERMEDIATION_ALL_COLUMN_KEYS];
      }
    });
  },
  onSelectOrClearSite: (type: SaleStatementTypeEnum) => {
    const {
      showHiddenFilters,
      getCategories,
      getGroups,
      getProducts,
      getSizes,
      getColors,
      getQualities,
    } = get();

    if (showHiddenFilters) {
      getCategories(type);
      getGroups(type);
      getProducts(type);
      getSizes(type);
      getColors();
      getQualities();
    }
  },
  toggleFilterVisibility: (type: SaleStatementTypeEnum) => {
    const { isMainFiltersSuccessful, loadHiddenFilters, showHiddenFilters, setData } = get();

    if (showHiddenFilters) {
      setData(state => {
        state.showHiddenFilters = false;
      });

      return;
    }

    if (isMainFiltersSuccessful) {
      const wasFilterHidden = !showHiddenFilters;

      setData(state => {
        state.showHiddenFilters = !showHiddenFilters;
      });

      if (wasFilterHidden) {
        loadHiddenFilters(type);
      }
    }
  },
  loadMainFilters: async () => {
    try {
      const [sitesResult, saleChannelsResult] = await Promise.allSettled([
        get().fetchSites(),
        get().fetchSaleChannels(),
      ]);

      const isSuccessful = (
        result:
          | PromiseRejectedResult
          | PromiseFulfilledResult<ISalesChannel[]>
          | PromiseFulfilledResult<Site[]>,
      ) => result.status === 'fulfilled';

      const isMainFiltersSuccessful = isSuccessful(sitesResult) && isSuccessful(saleChannelsResult);

      get().setData(state => {
        state.isMainFiltersSuccessful = isMainFiltersSuccessful;
      });

      if (!isMainFiltersSuccessful) {
        message.warn('Erro ao carregar alguns filtros principais.');
      }
    } catch (error) {
      console.error('Erro ao carregar filtros principais.', error);
    }
  },
  loadHiddenFilters: async (type: SaleStatementTypeEnum) => {
    const {
      getCategories,
      getGroups,
      getProducts,
      getSizes,
      getColors,
      getQualities,
      getCustomers,
    } = get();

    try {
      const [
        categoriesResult,
        groupsResult,
        productsResult,
        sizesResult,
        colorsResult,
        qualitiesResult,
        customersResult,
      ] = await Promise.allSettled([
        getCategories(type),
        getGroups(type),
        getProducts(type),
        getSizes(type),
        getColors(),
        getQualities(),
        getCustomers(),
      ]);

      const isSuccessful = (
        result:
          | PromiseRejectedResult
          | PromiseFulfilledResult<IProductCategory[]>
          | PromiseFulfilledResult<IProductSimple[]>
          | PromiseFulfilledResult<RecipientDetailed[]>
          | PromiseFulfilledResult<IProductGroupWithCategories[]>
          | PromiseFulfilledResult<ProductPreferenceColor[]>
          | PromiseFulfilledResult<ProductQuality[]>,
      ) => result.status === 'fulfilled';

      const allSuccessful =
        isSuccessful(categoriesResult) &&
        isSuccessful(groupsResult) &&
        isSuccessful(productsResult) &&
        isSuccessful(sizesResult) &&
        isSuccessful(colorsResult) &&
        isSuccessful(qualitiesResult) &&
        isSuccessful(customersResult);

      get().setData(state => {
        state.isHiddenFiltersSucessful = allSuccessful;
      });

      if (!allSuccessful) {
        console.warn('Erro ao carregar alguns filtros avançados.');
      }
    } catch (error) {
      console.error('Erro ao carregar filtros avançados.', error);
    }
  },
  resetStatesToDefault: () => {
    const { setData } = get();

    setData(state => {
      state.intermediationTable = initialStateTable;
      state.klokTable = initialStateTable;
      state.filters = initialStateFilters;
      state.intermediationFormFilterInstance = initialState.formFilterInstance;
      state.klokFormFilterInstance = initialState.formFilterInstance;
      state.isMainFiltersSuccessful = false;
      state.isHiddenFiltersSucessful = false;
      state.showHiddenFilters = false;
      state.intermediationVisibleColumnKeys = getIntermediationVisibleColumnKeys();
      state.klokVisibleColumnKeys = getKlokVisibleColumnKeys();
    });
  },
}));

export default useSaleStatementByTypeStore;
